"use client";

import { useMemo, useRef, useState } from "react";
import { z } from "zod";
import {
  evaluateAssessment,
  type AssessmentAnswerValue,
  type AssessmentQuestion,
  type AssessmentResponses,
  type AssessmentSchema,
} from "@/lib/gamification/assessment-engine";
import AssessmentResultCard, { type AssessmentResultCardCopy } from "@/components/gamification/AssessmentResultCard";
import {
  captureGamificationResultForCrm,
  gamificationTrackingAttributes,
  trackGamificationEngagement,
  type GamificationTrackingContext,
} from "@/components/gamification/tracking";
import PlayableExperienceCanvas from "@/components/gamification/PlayableExperienceCanvas";
import { useWebMcpTool } from "@/lib/webmcp/react";

export type AssessmentToolCopy = AssessmentResultCardCopy & {
  readonly eyebrow: string;
  readonly title: string;
  readonly intro: string;
  readonly progressLabel: string;
  readonly progressValueLabel: string;
  readonly optionalLabel: string;
  readonly previousLabel: string;
  readonly nextLabel: string;
  readonly viewResultLabel: string;
  readonly resetLabel: string;
  readonly resultCtaHrefTemplate?: string;
};

type AssessmentToolProps = {
  readonly copy: AssessmentToolCopy;
  readonly schema: AssessmentSchema;
  readonly tracking: GamificationTrackingContext;
};

function formatTemplate(template: string, values: Readonly<Record<string, string | number>>) {
  return Object.entries(values).reduce((text, [key, value]) => text.replaceAll(`{${key}}`, String(value)), template);
}

function isSelected(question: AssessmentQuestion, answer: AssessmentAnswerValue, optionId: string) {
  if (question.kind === "multi") {
    return Array.isArray(answer) && answer.includes(optionId);
  }

  if (typeof answer === "boolean") {
    return question.options.some((option) => option.id === optionId && option.booleanValue === answer);
  }

  return answer === optionId;
}

export default function AssessmentTool({ copy, schema, tracking }: AssessmentToolProps) {
  const [responses, setResponses] = useState<AssessmentResponses>({});
  const [stepIndex, setStepIndex] = useState(0);
  const [resultVisible, setResultVisible] = useState(false);
  const startedRef = useRef(false);
  const resultTrackedRef = useRef(false);
  const question = schema.questions[stepIndex] ?? schema.questions[0];
  const result = useMemo(() => evaluateAssessment(schema, responses), [responses, schema]);
  const resultCopy = copy.resultCtaHrefTemplate
    ? {
        ...copy,
        exportHref: formatTemplate(copy.resultCtaHrefTemplate, {
          result_tier: result.tier.id,
          tool_key: tracking.toolKey ?? schema.id,
        }),
      }
    : copy;
  const progressText = formatTemplate(copy.progressValueLabel, {
    answered: result.progress.answered,
    total: result.progress.total,
    percent: result.progress.percent,
  });
  const canMoveNext = question ? question.required === false || result.questionResults[stepIndex]?.answered : false;
  const currentQuestionResult = result.questionResults[stepIndex];
  const answeredByQuestion = new Map(result.questionResults.map((item) => [item.questionId, item.answered]));
  const canvasNodes = schema.questions.map((item, index) => ({
    id: item.id,
    label: item.label,
    detail: item.description,
    active: index === stepIndex,
    complete: answeredByQuestion.get(item.id) === true,
  }));
  const canvasTracks = question.options.map((option) => ({
    id: option.id,
    label: option.label,
    detail: option.description,
    active: currentQuestionResult?.selectedOptionIds.includes(option.id) === true,
    complete: currentQuestionResult?.selectedOptionIds.includes(option.id) === true,
  }));
  const toolSchema = useMemo(
    () =>
      z
        .object(
          Object.fromEntries(
            schema.questions.map((item) => {
              const optionIds = item.options.map((option) => option.id) as [string, ...string[]];
              return [item.id, item.kind === "multi" ? z.array(z.enum(optionIds)) : z.enum(optionIds)];
            }),
          ) as z.ZodRawShape,
        )
        .strict(),
    [schema.questions],
  );
  useWebMcpTool({
    action: "score_assessment",
    instance: schema.id.replaceAll("-", "_"),
    schema: toolSchema,
    execute: (input) => {
      const nextResponses = input as AssessmentResponses;
      const nextResult = evaluateAssessment(schema, nextResponses);
      setResponses(nextResponses);
      setStepIndex(Math.max(0, schema.questions.length - 1));
      setResultVisible(true);
      return { status: "success", uiState: nextResult };
    },
  });

  function trackStart() {
    if (startedRef.current) {
      return;
    }

    startedRef.current = true;
    trackGamificationEngagement(tracking, `${tracking.componentId}.start`, { xp_zone: "progress" });
  }

  function updateResponse(questionId: string, value: AssessmentAnswerValue) {
    trackStart();
    setResultVisible(false);
    setResponses((current) => ({ ...current, [questionId]: value }));
    trackGamificationEngagement(tracking, `${tracking.componentId}.step.${questionId}`, { xp_zone: "progress" });
  }

  function toggleMulti(questionId: string, optionId: string) {
    const current = responses[questionId];
    const selected = Array.isArray(current) ? current : [];
    updateResponse(questionId, selected.includes(optionId) ? selected.filter((id) => id !== optionId) : [...selected, optionId]);
  }

  function showResult() {
    setResultVisible(true);
    captureGamificationResultForCrm(tracking, result.tier.id);
    if (!resultTrackedRef.current) {
      resultTrackedRef.current = true;
      trackGamificationEngagement(tracking, `${tracking.componentId}.result`, {
        result_tier: result.tier.id,
        variant_key: result.tier.id,
        xp_zone: "panel",
      });
    }
  }

  function reset() {
    setResponses({});
    setStepIndex(0);
    setResultVisible(false);
    resultTrackedRef.current = false;
    startedRef.current = false;
  }

  if (!question) {
    return null;
  }

  return (
    <section
      id={schema.id}
      className="redesign-section redesign-section--muted gamification-tool"
      aria-labelledby={`${schema.id}-title`}
      data-gamification-tool={schema.id}
      data-gamification-mode={schema.mode ?? "standard"}
      {...gamificationTrackingAttributes(tracking)}
    >
      <div className="container">
        <div className="marketing-section-heading marketing-section-heading--left">
          <p className="marketing-eyebrow">{copy.eyebrow}</p>
          <h2 id={`${schema.id}-title`}>{copy.title}</h2>
          <p>{copy.intro}</p>
        </div>

        <div className="gamification-tool__grid">
          <div className="gamification-tool__panel redesign-list-card">
            <div
              className="gamification-tool__progress"
              role="progressbar"
              aria-label={copy.progressLabel}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={result.progress.percent}
            >
              <span style={{ width: `${result.progress.percent}%` }} />
            </div>
            <p className="gamification-tool__progress-label">{progressText}</p>

            <fieldset className="gamification-tool__fieldset">
              <legend>
                {question.label}
                {question.required === false ? <small>{copy.optionalLabel}</small> : null}
              </legend>
              {question.description ? <p>{question.description}</p> : null}
              <div className="gamification-tool__options">
                {question.options.map((option) => {
                  const checked = isSelected(question, responses[question.id], option.id);
                  const inputType = question.kind === "multi" ? "checkbox" : "radio";

                  return (
                    <label className="gamification-tool__option" key={option.id}>
                      <input
                        type={inputType}
                        name={question.id}
                        checked={checked}
                        onChange={() =>
                          question.kind === "multi"
                            ? toggleMulti(question.id, option.id)
                            : updateResponse(question.id, option.booleanValue ?? option.id)
                        }
                      />
                      <span>
                        <strong>{option.label}</strong>
                        {option.description ? <small>{option.description}</small> : null}
                      </span>
                    </label>
                  );
                })}
              </div>
            </fieldset>

            <div className="gamification-tool__actions">
              <button
                type="button"
                className="marketing-button marketing-button--secondary"
                disabled={stepIndex === 0}
                onClick={() => setStepIndex((current) => Math.max(0, current - 1))}
              >
                {copy.previousLabel}
              </button>
              {stepIndex < schema.questions.length - 1 ? (
                <button
                  type="button"
                  className="marketing-button marketing-button--primary"
                  disabled={!canMoveNext}
                  onClick={() => setStepIndex((current) => Math.min(schema.questions.length - 1, current + 1))}
                >
                  {copy.nextLabel}
                </button>
              ) : (
                <button
                  type="button"
                  className="marketing-button marketing-button--primary"
                  disabled={!result.progress.complete}
                  onClick={showResult}
                >
                  {copy.viewResultLabel}
                </button>
              )}
              <button type="button" className="gamification-tool__link-button" onClick={reset}>
                {copy.resetLabel}
              </button>
            </div>
          </div>

          <PlayableExperienceCanvas
            ariaLabel={copy.title}
            id={schema.id}
            nodes={canvasNodes}
            primaryLabel={resultVisible ? result.tier.label : question.label}
            progress={result.progress.percent}
            secondaryLabel={resultVisible ? result.tier.summary : question.description || copy.intro}
            tracks={canvasTracks}
            variant="assessment"
          />

          {resultVisible ? (
            <AssessmentResultCard
              copy={resultCopy}
              id={schema.id}
              result={result}
              onExportIntent={
                copy.exportLabel
                  ? () =>
                      trackGamificationEngagement(tracking, `${tracking.componentId}.export-intent`, {
                        result_tier: result.tier.id,
                        variant_key: result.tier.id,
                        xp_zone: "cta",
                      })
                  : undefined
              }
            />
          ) : null}
        </div>
      </div>
    </section>
  );
}
